import React from 'react';
import { View, Animated, TouchableWithoutFeedback } from 'react-native';

import PropTypes from 'prop-types';

import {
  Icon,
  VFText,
  PercentageIndicator,
  VFButton,
} from '@vfgroup-oneplatform/foundation/Components';
import { LightThemeColors } from '@vfgroup-oneplatform/foundation/Components/Themes';
import { withTheme } from '@vfgroup-oneplatform/foundation/Components/Themes/Provider';

import { USAGE_TYPES } from '../../../BusinessOverview/Utils/Enums';
import SohoFrameworkTestKeys from '../../../Utils/SohoFrameworkTestKeys';

import styles from './DashboardUsageInfoCard.Styles';

const usageTypeKeys = {
  [USAGE_TYPES.DATA]: {
    buttonTitle: 'soho_user_plan_tile_get_more_data_title',
    remainingUnits: 'soho_user_plan_tile_unlimited_unit_data_description',
  },
  [USAGE_TYPES.SMS]: {
    buttonTitle: 'soho_user_plan_tile_get_more_mins_title',
    remainingUnits: 'soho_user_plan_tile_unlimited_unit_sms_description',
  },
  [USAGE_TYPES.CALLS]: {
    buttonTitle: 'soho_user_plan_tile_get_more_sms_title',
    remainingUnits: 'soho_user_plan_tile_unlimited_unit_mins_description',
  },
};

function DashboardUsageInfoCard({
  icon,
  titlePlaceHolders,
  theme,
  style,
  remainingAmount,
  remainingUnits,
  totalValue,
  totalUnit,
  remainingDays,
  buttonTitle,
  usageType,
  isUnlimited,
  titleContainerStyle,
  onPress,
  ...props
}) {
  const renderUsageText = () => {
    if (isUnlimited) {
      return (
        <>
          <VFText
            i18nKey={'soho_user_plan_tile_unlimited_title'}
            style={styles.unlimitedRemainingTextStyle(theme)}
            numberOfLines={1}
            testKey={SohoFrameworkTestKeys.DBUsageCardUnlimitedTxt}
          />
          <VFText
            i18nKey={usageTypeKeys[usageType].remainingUnits}
            style={styles.unlimitedRemainingUnitTextStyle(theme)}
          />
        </>
      );
    }
    return (
      <VFText numberOfLines={1}>
        <VFText i18nKey={`${remainingAmount}`} style={styles.remainingTextStyle(theme)} />
        <VFText i18nKey={remainingUnits} style={styles.remainingUnitTextStyle(theme)} />
        <View style={styles.spacingViewStyle} />
        <VFText
          i18nKey={'soho_user_plan_tile_left_of'}
          placeHolders={[`${totalValue}${totalUnit}`]}
          style={styles.usageLeftTextStyle(theme)}
          testKey={SohoFrameworkTestKeys.DBUsageCardLeftOfTxt}
        />
      </VFText>
    );
  };
  const renderFooter = () => {
    return (
      <>
        {isUnlimited ? (
          <VFText
            i18nKey={'soho_user_plan_tile_unlimited_description'}
            style={styles.unlimitedFooterTextStyle}
            numberOfLines={1}
            testKey={SohoFrameworkTestKeys.DBUsageCardFooterTxtUnlimited}
          />
        ) : (
          <VFText
            i18nKey={
              remainingDays === 1
                ? 'soho_user_plan_tile_reset_in_day'
                : 'soho_user_plan_tile_reset_in_days'
            }
            placeHolders={[remainingDays]}
            style={styles.footerTextStyle(theme)}
            numberOfLines={1}
            testKey={SohoFrameworkTestKeys.DBUsageCardFooterTxt}
          />
        )}
        {remainingAmount === 0 ? (
          <VFButton
            title={usageTypeKeys[usageType].buttonTitle}
            type="primary"
            textStyle={styles.buttonTextStyle}
            style={styles.buttonStyle}
            testKey={SohoFrameworkTestKeys.DBUsageCardAddMoreBtn}
          />
        ) : (
          <View style={styles.buttonSpace} />
        )}
      </>
    );
  };
  const getValuePercentage = () => {
    if (isUnlimited) {
      return 100;
    }
    if (isNaN(remainingAmount / totalValue)) {
      return 0;
    }
    return (remainingAmount / totalValue) * 100;
  };

  return (
    <TouchableWithoutFeedback onPress={onPress}>
      <Animated.View style={[styles.containerStyle(theme), style]}>
        <Animated.View style={[styles.titleContainerStyle, titleContainerStyle]}>
          <Icon name={icon} />
          <VFText
            i18nKey={'soho_user_plan_tile_top_title'}
            placeHolders={titlePlaceHolders}
            style={styles.titleTextStyle(theme)}
            numberOfLines={1}
          />
        </Animated.View>
        <View>
          <View style={styles.usageContainerStyle}>{renderUsageText()}</View>
          <PercentageIndicator
            value={getValuePercentage()}
            fillColor={theme.colors.fillColor}
            color={theme.colors.primaryColor}
            {...props}
          />
        </View>
        <View style={styles.footerContainer}>{renderFooter()}</View>
      </Animated.View>
    </TouchableWithoutFeedback>
  );
}

DashboardUsageInfoCard.defaultProps = {
  theme: LightThemeColors,
};

DashboardUsageInfoCard.propTypes = {
  icon: PropTypes.object.isRequired,
  buttonTitle: PropTypes.string,
  titlePlaceHolders: PropTypes.arrayOf(PropTypes.string),
  usageType: PropTypes.oneOf(Object.values(USAGE_TYPES)).isRequired,
  isUnlimited: PropTypes.bool.isRequired,
  remainingUnits: PropTypes.string,
  remainingAmount: PropTypes.number,
  totalUnit: PropTypes.string,
  totalValue: PropTypes.number,
  remainingDays: PropTypes.number,
  style: PropTypes.object,
  titleContainerStyle: PropTypes.object,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
  onPress: PropTypes.func,
};

export default withTheme(DashboardUsageInfoCard);
